import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, PhoneMissed, CheckCircle2, Send, MessageSquare } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { vpMissedSettings, vpMissedSettingsPatch, vpMissedList, vpMissedRecover } from "@/lib/api";

const field = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const btnPrimary = "inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 px-3 py-2 text-sm font-semibold transition";
const btnGhost = "inline-flex items-center gap-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 px-3 py-2 text-sm font-semibold transition";

export default function MissedCallConsole() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const reload = useCallback(async () => {
    setSettings(await vpMissedSettings());
    const l = await vpMissedList();
    setItems(l.items || []);
    setLoading(false);
  }, []);
  useEffect(() => { if (user) reload().catch(() => setLoading(false)); }, [user, reload]);

  if (user === null) return <Gate />;
  if (loading) return <Loading />;

  const save = async () => {
    setSaving(true);
    try {
      const s = await vpMissedSettingsPatch({
        enabled: settings.enabled,
        sms_template: settings.sms_template,
        auto_send_delay_seconds: Number(settings.auto_send_delay_seconds || 60),
        quiet_hours_start: settings.quiet_hours_start,
        quiet_hours_end: settings.quiet_hours_end,
        max_attempts: Number(settings.max_attempts || 2),
      });
      setSettings(s);
    } finally { setSaving(false); }
  };

  const recover = async (mid) => { await vpMissedRecover(mid); reload(); };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="missed-call-console">
        <div className="max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-indigo-700">
            <PhoneMissed className="w-3.5 h-3.5" /> Missed-Call Recovery
          </div>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">Recover every missed call</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">Auto-SMS + callback offer within seconds of a missed call. SMS delivery is <b>MOCKED</b> until your Sent.dm key is added.</p>

          <div className="mt-6 grid lg:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-bold text-slate-900 mb-3">Settings</div>
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input type="checkbox" checked={!!settings.enabled} onChange={(e) => setSettings({...settings, enabled: e.target.checked})} data-testid="mc-enabled" />
                <span className="text-sm text-slate-700">Auto-recovery enabled</span>
              </label>
              <label className="text-xs font-semibold text-slate-700 uppercase">SMS template</label>
              <textarea rows={3} value={settings.sms_template} onChange={(e) => setSettings({...settings, sms_template: e.target.value})} className={field + " mt-1"} data-testid="mc-template" />
              <div className="text-[11px] text-slate-500 mt-1">Placeholders: {"{name}"}, {"{business}"}, {"{callback_link}"}</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div><label className="text-xs font-semibold uppercase">Delay (sec)</label><input type="number" value={settings.auto_send_delay_seconds} onChange={(e) => setSettings({...settings, auto_send_delay_seconds: e.target.value})} className={field} data-testid="mc-delay" /></div>
                <div><label className="text-xs font-semibold uppercase">Max attempts</label><input type="number" value={settings.max_attempts} onChange={(e) => setSettings({...settings, max_attempts: e.target.value})} className={field} data-testid="mc-max" /></div>
                <div><label className="text-xs font-semibold uppercase">Quiet start</label><input value={settings.quiet_hours_start} onChange={(e) => setSettings({...settings, quiet_hours_start: e.target.value})} className={field} data-testid="mc-qstart" /></div>
                <div><label className="text-xs font-semibold uppercase">Quiet end</label><input value={settings.quiet_hours_end} onChange={(e) => setSettings({...settings, quiet_hours_end: e.target.value})} className={field} data-testid="mc-qend" /></div>
              </div>
              <div className="mt-4 flex justify-end">
                <button onClick={save} disabled={saving} className={btnPrimary} data-testid="mc-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Save</button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-bold text-slate-900 mb-3">Missed calls ({items.length})</div>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {items.length === 0 && <div className="text-xs text-slate-500">No missed calls yet.</div>}
                {items.map((m) => (
                  <div key={m.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`mc-item-${m.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{m.caller_name || "Unknown"} <span className="text-xs text-slate-500 ml-1">{m.caller_number}</span></div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{m.created_at?.slice(0, 16)} · {m.status}{m.recovered ? " · recovered" : ""}</div>
                      </div>
                      {!m.recovered && (
                        <button onClick={() => recover(m.id)} className={btnGhost} data-testid={`mc-recover-${m.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Mark recovered</button>
                      )}
                    </div>
                    {m.sms_body && <div className="mt-2 text-[11px] text-slate-600 italic border-l-2 border-indigo-200 pl-2">{m.sms_body}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Gate() { return <div className="min-h-screen flex flex-col"><Navbar /><div className="flex-1 grid place-items-center"><Link to="/login" className={btnPrimary}>Sign in</Link></div></div>; }
function Loading() { return <div className="min-h-screen flex flex-col"><Navbar /><div className="flex-1 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div></div>; }
